import React from "react";
import { Link } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Sparkles } from "lucide-react";

/**
 * Footer esteso ecosistema TotalKlick.
 * 4 colonne: Brand · Moduli · Conformità · Società/Recapiti
 * Coerente con stile cinematic dark.
 */
export default function Footer({ variant = "hub" }) {
    const { user } = useAuth();
    const isLogged = !!user && typeof user === "object";

    return (
        <footer
            className="relative w-full px-6 md:px-12 pt-16 md:pt-20 pb-8 border-t"
            style={{
                background: variant === "hub" ? "rgba(5,5,15,0.7)" : "rgba(10,16,36,0.92)",
                borderColor: "rgba(255,255,255,0.06)",
                fontFamily: "Outfit, sans-serif",
                zIndex: 3,
            }}
            data-testid="global-footer"
        >
            <div className="max-w-7xl mx-auto">
                <div className="grid grid-cols-1 md:grid-cols-12 gap-10 md:gap-8 mb-12">
                    {/* Brand */}
                    <div className="md:col-span-4">
                        <Link to="/" className="flex items-center mb-4" data-testid="footer-logo" aria-label="TotalKlick home">
                            <img
                                src="/brand/totalklick-logo.svg"
                                alt="TotalKlick"
                                className="h-7 w-auto"
                                style={{ filter: "drop-shadow(0 0 14px rgba(79,139,255,0.45))" }}
                            />
                        </Link>
                        <p className="text-white/55 text-[13px] leading-relaxed max-w-xs">
                            Il Sistema Operativo per le PMI italiane. Software, AI, marketing e gestione clienti in un unico ecosistema.
                        </p>
                        <Link
                            to="/aria"
                            className="mt-5 inline-flex items-center gap-1.5 px-3.5 py-2 rounded-full text-[12px] text-white/85 hover:text-white border border-white/12 hover:bg-white/5 transition"
                            data-testid="footer-aria-cta"
                            style={{ fontFamily: "Geist, sans-serif", letterSpacing: "0.02em" }}
                        >
                            <Sparkles size={12} /> Scopri Aria
                        </Link>
                    </div>

                    {/* Moduli */}
                    <div className="md:col-span-3">
                        <FooterColTitle>Moduli</FooterColTitle>
                        <ul className="space-y-2">
                            <FooterLink to="/totalpl" testid="footer-mod-totalpl">TotalPL · Tipografie</FooterLink>
                            <FooterLink to="/totalklickads" testid="footer-mod-ads">TotalKlickADS · Social AI</FooterLink>
                            <FooterLink to="/app/design" testid="footer-mod-design">Design &amp; Stampa</FooterLink>
                            <li>
                                <button
                                    type="button"
                                    onClick={() => window.dispatchEvent(new CustomEvent("open-aria"))}
                                    className="text-[12.5px] text-white/60 hover:text-white transition text-left"
                                    data-testid="footer-mod-offerte"
                                >
                                    OfferteInCittà
                                </button>
                            </li>
                            <FooterExternal href="https://elly.offerteincitta.it" testid="footer-mod-elly">EllyMenu</FooterExternal>
                            <FooterExternal href="https://gostreet.it" testid="footer-mod-gostreet">GoStreet</FooterExternal>
                            <FooterExternal href="https://photovendo.com" testid="footer-mod-photovendo">PhotoVendo</FooterExternal>
                        </ul>
                    </div>

                    {/* Conformità */}
                    <div className="md:col-span-2">
                        <FooterColTitle>Conformità</FooterColTitle>
                        <ul className="space-y-2">
                            <FooterLink to="/legal/privacy" testid="footer-legal-privacy">Privacy (GDPR)</FooterLink>
                            <FooterLink to="/legal/cookie" testid="footer-legal-cookie">Cookie Policy</FooterLink>
                            <FooterLink to="/legal/terms" testid="footer-legal-terms">Termini</FooterLink>
                            <FooterLink to="/legal/ai-act" testid="footer-legal-aiact">AI Act</FooterLink>
                            <FooterLink to="/legal/dpa" testid="footer-legal-dpa">DPA</FooterLink>
                        </ul>
                    </div>

                    {/* Società */}
                    <div className="md:col-span-3">
                        <FooterColTitle>TotalKlick srls</FooterColTitle>
                        <ul className="space-y-1.5 text-[12.5px] text-white/55">
                            <li>P.IVA / C.F. <span className="text-white/75">10559691216</span></li>
                            <li>REA <span className="text-white/75">NA-1114904</span></li>
                            <li>Cap. Soc. €2.500,00 i.v.</li>
                            <li>Corso A. Volta 26, 80040 Terzigno (NA)</li>
                            <li className="pt-1">
                                PEC: <a href="mailto:totalklicksrls24@pec.it" className="text-white/75 hover:text-white transition">totalklicksrls24@pec.it</a>
                            </li>
                            <li>
                                <a href="mailto:info@totalklick.it" className="text-white/75 hover:text-white transition">info@totalklick.it</a>
                            </li>
                            <li className="pt-2">
                                {isLogged ? (
                                    <Link to="/app" className="inline-flex items-center gap-1 text-[#4F8BFF] hover:text-white transition" data-testid="footer-workspace">
                                        → Apri Workspace
                                    </Link>
                                ) : (
                                    <Link to="/login" className="inline-flex items-center gap-1 text-[#4F8BFF] hover:text-white transition" data-testid="footer-login">
                                        → Accedi al Workspace
                                    </Link>
                                )}
                            </li>
                        </ul>
                    </div>
                </div>

                {/* Bottom strip */}
                <div className="pt-6 flex flex-col md:flex-row items-start md:items-center justify-between gap-3" style={{ borderTop: "1px solid rgba(255,255,255,0.05)" }}>
                    <div className="text-[10.5px] uppercase tracking-[0.16em] text-white/45" style={{ fontFamily: "JetBrains Mono, monospace" }}>
                        © {new Date().getFullYear()} TotalKlick® srls · Tutti i diritti riservati
                    </div>
                    <div className="flex items-center gap-4 text-[10.5px] text-white/45" style={{ fontFamily: "JetBrains Mono, monospace" }}>
                        <span>🇮🇹 Made in Italy</span>
                        <span>🔒 GDPR</span>
                        <span>🤖 AI Act</span>
                    </div>
                </div>
            </div>
        </footer>
    );
}

function FooterColTitle({ children }) {
    return (
        <div
            className="text-[10px] uppercase tracking-[0.22em] text-white/40 font-extrabold mb-4"
            style={{ fontFamily: "JetBrains Mono, monospace" }}
        >
            {children}
        </div>
    );
}

function FooterLink({ to, children, testid }) {
    return (
        <li>
            <Link to={to} className="text-[12.5px] text-white/60 hover:text-white transition" data-testid={testid}>
                {children}
            </Link>
        </li>
    );
}

function FooterExternal({ href, children, testid }) {
    return (
        <li>
            <a
                href={href}
                target="_blank"
                rel="nofollow noopener noreferrer"
                className="text-[12.5px] text-white/60 hover:text-white transition"
                data-testid={testid}
            >
                {children}
            </a>
        </li>
    );
}
